/**
 * TS side of the theme tokens (theme.css) plus allocation-free string tables for per-frame writes.
 * Per-frame code quantises a value to an integer and indexes a table, so the dom.ts
 * write-if-changed helpers compare interned strings and never build new ones.
 */

/** CSS colour for a class resource id (reads the --hk-res-* tokens). */
export function resourceVar(id: string | null | undefined): string {
  return RES_VARS[id ?? ''] ?? (id ? `var(--hk-res-${id}, var(--hk-res-default))` : 'var(--hk-res-default)');
}
const RES_VARS: Record<string, string> = {
  mana: 'var(--hk-res-mana)',
  rage: 'var(--hk-res-rage)',
  resolve: 'var(--hk-res-resolve)',
  maelstrom: 'var(--hk-res-maelstrom)',
  holy_power: 'var(--hk-res-holy_power)',
};

/** Icon size tokens (px at scale 1), mirrored from theme.css. */
export const ICON_PX = { xs: 22, sm: 30, md: 40, lg: 48 } as const;
export type IconSizeName = keyof typeof ICON_PX;

/** Sweep resolution: degrees 0..360 (1° steps are invisible at icon size). */
export const SWEEP_STEPS = 360;
/** DEG[i] = "<i>deg". */
export const DEG: readonly string[] = Array.from({ length: SWEEP_STEPS + 1 }, (_, i) => `${i}deg`);
/** Quantise a 0..1 fraction to a DEG index. */
export const sweepIndex = (frac: number): number => (frac > 0 ? Math.min(SWEEP_STEPS, Math.max(0, Math.round(frac * SWEEP_STEPS))) : 0);

/** Bar resolution (scaleX steps). 1000 = 0.1%: sub-pixel on a 600 px bar. */
export const BAR_STEPS = 1000;
/** SCALE_X[i] = "scaleX(<i/1000>)". */
export const SCALE_X: readonly string[] = Array.from({ length: BAR_STEPS + 1 }, (_, i) => `scaleX(${i / BAR_STEPS})`);
export const barIndex = (frac: number): number => (frac > 0 ? Math.min(BAR_STEPS, Math.max(0, Math.round(frac * BAR_STEPS))) : 0);

/** Small integer → string cache (charges, stacks, levels, whole seconds). */
const INT_STR: string[] = Array.from({ length: 1000 }, (_, i) => String(i));
export const intText = (n: number): string => (n >= 0 && n < 1000 && Number.isInteger(n) ? INT_STR[n]! : String(n));
/** Tenths 0.0..9.9 for sub-10 s timers: TENTHS[i] = (i/10).toFixed(1). */
export const TENTHS: readonly string[] = Array.from({ length: 100 }, (_, i) => (i / 10).toFixed(1));
